import { saveLinkAsFile, transmissionTaskPool } from '@core/Utils'
import type { AbsDownloadTask } from '@/core/transmission/AbsDownloadTask'
import type { FileInfo, IReadOnlyDownloadManager } from '@core/transmission/IReadOnlyFileTransmissionManager'
import { DownloadTaskDirectly } from '@/core/transmission/DownloadTaskDirectly'
import { DownloadTaskWithCloud } from '@core/transmission/DownloadTaskWithCloud'
import { INSTANCE as DeviceManager } from '../devices/DeviceManager'
import { AvailableType } from '../devices/IDevice'
import { AbsFileTransmissionManager } from '@core/transmission/AbsFileTransmissionManager'
import { INSTANCE as certificateManager, TransmissionType } from '@core/transmission/CertificateManager'
import { INSTANCE as DeviceConnectionKeeper } from '@core/devices/DeviceConnectionKeeper'
import { DeviceTaskError, type BaseTaskInfo, type GroupFileInfo, type SpecificTaskInfo } from './ITask'
import { isEmpty } from 'lodash-es'
import { DownloadTaskZip } from './DownloadTaskZip'

export class FileDownloadManager extends AbsFileTransmissionManager<AbsDownloadTask> {
  constructor() {
    super()
  }

  getManager(appId: string): IReadOnlyDownloadManager {
    return {
      append: (
        deviceId: string,
        fileInfos: FileInfo[],
        isViaCloud: boolean,
        isSupportZip?: boolean,
        isSupportRange?: boolean
      ) =>
        this.append(
          {
            appId,
            deviceId,
            fileInfos,
            isViaCloud,
          },
          undefined,
          isSupportZip,
          isSupportRange
        ),
      appendGroupFile: (deviceId: string, groupFileInfo: GroupFileInfo, isViaCloud: boolean) =>
        this.appendGroupFile(appId, deviceId, groupFileInfo, isViaCloud),
    }
  }

  async append(
    taskInfo: BaseTaskInfo,
    specificTaskInfo?: SpecificTaskInfo,
    isSupportZip?: boolean,
    isSupportRange?: boolean
  ): Promise<void> {
    const { appId, deviceId, fileInfos, isViaCloud = false } = taskInfo

    if (isEmpty(appId) || isEmpty(deviceId) || isEmpty(fileInfos)) return

    const device = DeviceManager.getDeviceById(deviceId)

    if (!device) return
    if (this._isDisableAppendTask(device)) return
    device.isSupportZip = isSupportZip ?? false
    const lanAvailable = await device.getLanAvailableInfo()

    console.log(
      `[Transmission]: append 添加下载任务 ${new Date().toLocaleString()}
    lanAvailable: ${lanAvailable}
    isSupportZip: ${isSupportZip}
    isSupportRange: ${isSupportRange}
    taskInfo:
    `,
      taskInfo,
      'specificTaskInfo:',
      specificTaskInfo
    )

    if (lanAvailable === AvailableType.REACHABLE && !isViaCloud) {
      console.log(`[Transmission]: append 添加局域网下载任务 ${new Date().toLocaleString()}`, taskInfo)

      this.#createDirectlyTask(taskInfo, specificTaskInfo, isSupportRange)
    } else {
      console.log(`[Transmission]: append 添加公网下载任务 ${new Date().toLocaleString()}`, taskInfo)

      this.#createOcsTask(taskInfo, specificTaskInfo)
    }

    if (lanAvailable === AvailableType.REACHABLE_ERROR_CERTIFICATION) {
      certificateManager.transmissionType = TransmissionType.FILE_DOWNLOAD
      certificateManager.showLANAvailabilityNotice({ extraQuery: `deviceId=${device.id}` })
    }
  }

  async appendGroupFile(
    appId: string,
    deviceId: string,
    groupFileInfo: GroupFileInfo,
    isViaCloud = false
  ): Promise<void> {
    if (isEmpty(appId) || isEmpty(deviceId) || isEmpty(groupFileInfo)) return

    const device = DeviceManager.getDeviceById(deviceId)

    if (!device) return
    if (this._isDisableAppendTask(device)) return
    device.isSupportZip = true

    this.#createZipTask(appId, deviceId, groupFileInfo, isViaCloud)

    const lanAvailable = await device.getLanAvailableInfo()

    if (lanAvailable === AvailableType.REACHABLE_ERROR_CERTIFICATION) {
      certificateManager.transmissionType = TransmissionType.FILE_DOWNLOAD
      certificateManager.showLANAvailabilityNotice({ extraQuery: `deviceId=${device.id}` })
    }
  }

  protected async _onSuccess(task: AbsDownloadTask): Promise<void> {
    const taskInfo = task.toJson()

    if (task instanceof DownloadTaskWithCloud) {
      task.removeUrlTag()
    }
    transmissionTaskPool.addTask(() => {
      taskInfo.objectUrl && saveLinkAsFile(taskInfo.objectUrl, taskInfo.fileName)
    })
  }

  #setTaskDownloadListener(task: AbsDownloadTask): void {
    task.setDownloadListener({
      onError: (deviceId, targetDir, fileName, reason) => {
        if (reason === DeviceTaskError.DEVICE_CANCEL) {
          this._taskScheduler.removeTask(task, false, false)
        }
      },
      onSuccess: (deviceId, targetDir, fileName, task) => {
        if (task instanceof DownloadTaskZip) {
          const mirrorTask = task.copy()

          this._taskScheduler.removeTask(task, false, false)

          if (mirrorTask) {
            this.append(mirrorTask, mirrorTask, true, true)
          }
        }
      },
      onProgressChanged: (transmitted, total, deviceId, isTransmissionToOCS) => {
        // 根据进度通知端侧保持端口开启
        DeviceConnectionKeeper.keepDeviceConnectById(deviceId)
      },
    })
  }

  #createDirectlyTask(baseTaskInfo: BaseTaskInfo, mirrorTask?: SpecificTaskInfo, isSupportRange?: boolean) {
    const { deviceId, fileInfos, appId } = baseTaskInfo

    for (const fileInfo of fileInfos) {
      if (isEmpty(fileInfo.parentPath) || isEmpty(fileInfo.fileName)) continue

      const task = new DownloadTaskDirectly(deviceId, fileInfo, appId, mirrorTask, isSupportRange)

      this._taskScheduler?.addTask(task)
      this.#setTaskDownloadListener(task)
    }
  }

  async #createOcsTask(baseTaskInfo: BaseTaskInfo, mirrorTask?: SpecificTaskInfo) {
    const { deviceId, fileInfos, appId } = baseTaskInfo

    for (const fileInfo of fileInfos) {
      const { parentPath, fileName } = fileInfo

      if (isEmpty(parentPath) || isEmpty(fileName)) continue

      const task = new DownloadTaskWithCloud(deviceId, fileInfo, appId, mirrorTask)

      this._taskScheduler?.addTask(task)
      this.#setTaskDownloadListener(task)
    }
  }

  async #createZipTask(appId: string, deviceId: string, groupFileInfo: GroupFileInfo, isViaCloud = false) {
    if (isEmpty(groupFileInfo.files) || isEmpty(groupFileInfo.fileName)) return

    const task = new DownloadTaskZip(deviceId, groupFileInfo, appId, isViaCloud)

    this._taskScheduler?.addTask(task)
    this.#setTaskDownloadListener(task)
  }
}

const INSTANCE = new FileDownloadManager()

export { INSTANCE }
